import { useMemo, useState } from 'react'

import { useFloorPlan } from '../../hooks/useFloorPlan'
import { usePermissions } from '@/hooks/usePermissions'
import { ImPlus } from 'react-icons/im'
import { MdQrCode } from 'react-icons/md'
import { useSelector } from 'react-redux'

import {
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import {
  Button,
  DragEventI,
  DrawerScrollContainer,
  SearchInput,
  Switch,
} from '@/components/ui'
import { CheckpointIProps } from '@/features/floor-plan'
import {
  CheckpointListItem,
  UnPlacedCheckpointListItem,
} from '@/features/guard-tour'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode } from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import {
  getPlacedCheckpoints,
  getUnPlacedCheckpoints,
} from '../../utils/filterCheckpoints'
import { CreateCheckPointsModal } from './CreateCheckPointsModal'
import { NoCheckPointView } from './NoCheckPointView'

interface CheckPointsPanelIProps {
  facilityId: string
  checkpoints: CheckpointIProps[]
  onTileDragStart: (event: DragEventI) => void
  onCheckPointClick: (checkpoint: CheckpointIProps) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

export const CheckPointList = ({
  facilityId,
  checkpoints,
  onCheckPointClick,
  onTileDragStart,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: CheckPointsPanelIProps) => {
  const me = useSelector(selectMe)
  const isEditMode = useSelector(selectEditMode)
  const { shouldEnableCreateCheckpoint } = usePermissions()
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure()
  const { floorId } = useFloorPlan()
  const [shouldShowPlaced, setShowPlaced] = useState(true)
  const [filter, setFilter] = useState('')

  const placedCheckpoints: CheckpointIProps[] = useMemo(() => {
    return getPlacedCheckpoints(checkpoints, filter, floorId)
  }, [checkpoints, filter, shouldShowPlaced])

  const unplacedCheckpoints: CheckpointIProps[] = useMemo(() => {
    return getUnPlacedCheckpoints(checkpoints, filter)
  }, [checkpoints, filter, shouldShowPlaced])

  const hasPlacedCheckpoint = placedCheckpoints?.length > 0
  const hasUnplacedCheckpoint = unplacedCheckpoints?.length > 0

  const onToggleChange = () => {
    setShowPlaced(!shouldShowPlaced)
  }

  const handleOnClick = (checkpoint: CheckpointIProps) => {
    onCheckPointClick(checkpoint)
  }

  return (
    <>
      <Flex
        align='center'
        borderBottom='1px solid rgba(9, 30, 66, 0.14)'
        direction='column'
        padding='30px'
      >
        <VStack alignItems='start' spacing='10px' w='full'>
          <HStack>
            <MdQrCode color='#44546F' size={26} />
            <Text
              color='#1F2023'
              fontSize='20px'
              fontWeight={700}
              lineHeight='30px'
            >
              Checkpoints
            </Text>
          </HStack>
          <Text
            color='#1F2023'
            fontSize='12px'
            fontWeight={300}
            lineHeight='18px'
            pb='5px'
          >
            {shouldEnableCreateCheckpoint
              ? `All placed and unplaced Checkpoints are displayed here. You can also create and configure Checkpoints.`
              : `All placed and unplaced Checkpoints are displayed here.`}
          </Text>
          <Button
            isDisabled={!isEditMode || !shouldEnableCreateCheckpoint}
            isFullWidth
            justifyContent='flex-start'
            leftIcon={
              <Box mr='15px'>
                <ImPlus size={12} />
              </Box>
            }
            onClick={() => {
              onModalOpen()
              mixpanel.track(
                'Button Press: Facility Checkpoints - Create Checkpoint',
                {
                  facility_id: facilityId,
                  role: me.roleName,
                }
              )
            }}
            variant='affirm'
          >
            Create Checkpoint
          </Button>
        </VStack>
      </Flex>
      <DrawerScrollContainer mb='40px' px='30px' py='20px'>
        <Box alignItems='center' display='flex'>
          <Switch
            backgroundColor='#2fb2b0'
            ballHeight='12px'
            ballPadding='2px'
            checked={!shouldShowPlaced}
            dataTestId='Switch:input'
            height='16px'
            onChange={onToggleChange}
            width='40px'
          />
          <Box
            color='#667289'
            fontSize='11px'
            fontWeight={500}
            lineHeight='16px'
            ml='10px'
          >
            Show only unplaced checkpoints
          </Box>
        </Box>
        <Box mb='20px' mt='20px'>
          <SearchInput
            onChange={(e) => setFilter(e.target.value)}
            placeholder='Filter by name'
            showIcon={false}
            value={filter}
          />
        </Box>
        <AnimatePresence>
          {[...(unplacedCheckpoints ?? []), ...(placedCheckpoints ?? [])]
            .length > 0 ? (
            <>
              {shouldShowPlaced && placedCheckpoints?.length > 0 && (
                <Box
                  animate={{ opacity: 1 }}
                  as={motion.div}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  w='full'
                >
                  <Text
                    color='#44546F'
                    fontSize='16px'
                    fontWeight={500}
                    letterSpacing='-0.4px'
                    lineHeight='24px'
                    my='20px'
                  >
                    Current Floor
                  </Text>
                  <VStack align='stretch' spacing='10px'>
                    {hasPlacedCheckpoint ? (
                      placedCheckpoints?.map((c) => (
                        <CheckpointListItem
                          id={c.id}
                          isEditMode={isEditMode}
                          key={c.id}
                          label={c.name}
                          onClick={() => handleOnClick(c)}
                          onDeviceItemHoverIn={onDeviceItemHoverIn}
                          onDeviceItemHoverOut={onDeviceItemHoverOut}
                          placed={c.isPlaced}
                          subLabel={c.displayId}
                        />
                      ))
                    ) : (
                      <NoCheckPointView title='No checkpoints placed on this floor' />
                    )}
                  </VStack>
                </Box>
              )}
              {unplacedCheckpoints?.length > 0 && (
                <Box
                  animate={{ opacity: 1 }}
                  as={motion.div}
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  w='full'
                >
                  <Text
                    color='#44546F'
                    fontSize='16px'
                    fontWeight={500}
                    letterSpacing='-0.4px'
                    lineHeight='24px'
                    my='20px'
                  >
                    Unplaced
                  </Text>
                  <VStack align='stretch' spacing='10px'>
                    {hasUnplacedCheckpoint ? (
                      unplacedCheckpoints?.map((c) => (
                        <UnPlacedCheckpointListItem
                          checkpoint={c}
                          isEditMode={isEditMode}
                          key={c?.id}
                          onClick={handleOnClick}
                          onTileDragStart={onTileDragStart}
                        />
                      ))
                    ) : (
                      <NoCheckPointView title='No unplaced checkpoints on this floor' />
                    )}
                  </VStack>
                </Box>
              )}
            </>
          ) : (
            <Text
              animate={{ opacity: 1 }}
              as={motion.div}
              color='#9b9b9b'
              exit={{ opacity: 0 }}
              fontSize='14px'
              fontWeight={500}
              initial={{ opacity: 0 }}
              letterSpacing='-0.4px'
              lineHeight='16px'
              my='30px'
              textAlign='center'
            >
              List is empty
            </Text>
          )}
        </AnimatePresence>
      </DrawerScrollContainer>
      {isModalOpen && (
        <CreateCheckPointsModal isOpen={isModalOpen} onClose={onModalClose} />
      )}
    </>
  )
}
